import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ShareNetwork,
  Copy,
  Check,
  User,
  Coins,
  ArrowsClockwise,
  Gift,
  Trophy,
  PencilSimple,
  LinkSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Stat Card ─── */
const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="glass-light rounded-xl p-4">
    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
      <Icon className={`w-4 h-4 ${accent}`} weight="fill" />
      {label}
    </div>
    <p className={`text-2xl font-bold ${accent}`}>{value}</p>
  </div>
);

const ReferralPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Custom code editing
  const [editingCode, setEditingCode] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);

  // Active tab
  const [tab, setTab] = useState('referrals');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, referralsRes, commissionsRes] = await Promise.all([
        axios.get(`${API}/api/referrals/stats`, { withCredentials: true }),
        axios.get(`${API}/api/referrals/list`, { withCredentials: true }),
        axios.get(`${API}/api/referrals/commissions`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setReferrals(referralsRes.data);
      setCommissions(commissionsRes.data);
    } catch {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const referralLink = `${window.location.origin}?ref=${stats?.referral_code || ''}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const saveCustomCode = async () => {
    if (!customCode.trim()) return;
    setSavingCode(true);
    try {
      const res = await axios.post(
        `${API}/api/referrals/custom-code`,
        { code: customCode },
        { withCredentials: true }
      );
      setStats(prev => ({ ...prev, referral_code: res.data.referral_code }));
      setEditingCode(false);
      toast.success('Custom code saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save code');
    } finally {
      setSavingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="referral-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
          <ShareNetwork weight="fill" className="w-8 h-8 text-emerald-400" />
          Affiliate Program
        </h1>
        <p className="text-slate-400 mt-2 max-w-lg">
          Earn <span className="text-emerald-400 font-bold">5% commission</span> on every token purchase made by people you refer. Lifetime earnings, no cap.
        </p>
      </div>

      {/* Referral Link Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-light rounded-2xl p-6 mb-6"
        data-testid="referral-link-card"
      >
        <h2 className="text-white font-bold mb-4 flex items-center gap-2">
          <LinkSimple className="w-5 h-5 text-emerald-400" /> Your Referral Link
        </h2>

        <div className="flex gap-2">
          <div className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-300 font-mono truncate" data-testid="referral-link-display">
            {referralLink}
          </div>
          <button
            onClick={copyLink}
            className={`px-4 rounded-xl font-semibold flex items-center gap-2 transition-colors ${
              copied ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-white hover:bg-slate-700'
            }`}
            data-testid="copy-referral-link"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        {/* Custom code */}
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm text-slate-500">Code:</span>
          {editingCode ? (
            <div className="flex items-center gap-2">
              <Input
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                placeholder="YOUR-CODE"
                className="bg-slate-900 border-white/10 text-sm w-40 uppercase"
                maxLength={20}
                data-testid="custom-code-input"
              />
              <button
                onClick={saveCustomCode}
                disabled={savingCode || !customCode.trim()}
                className="px-3 py-1.5 bg-emerald-500 text-white text-sm rounded-lg font-semibold disabled:opacity-50"
                data-testid="save-custom-code"
              >
                {savingCode ? '...' : 'Save'}
              </button>
              <button onClick={() => setEditingCode(false)} className="text-slate-500 text-sm hover:text-white">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-emerald-400 font-bold font-mono" data-testid="referral-code">{stats?.referral_code}</span>
              <button
                onClick={() => { setCustomCode(stats?.referral_code || ''); setEditingCode(true); }}
                className="text-slate-500 hover:text-white"
                data-testid="edit-code-btn"
              >
                <PencilSimple className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Social Share Buttons */}
        <div className="mt-4 pt-4 border-t border-white/5">
          <p className="text-xs text-slate-500 mb-3 uppercase tracking-wider font-semibold">Share on</p>
          <div className="flex flex-wrap gap-2">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Join me on LustLink! Creators keep 90% of revenue. Sign up with my link:')}&url=${encodeURIComponent(referralLink)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-[#1d9bf0]/20 hover:text-[#1d9bf0] text-slate-300 text-sm font-semibold rounded-xl transition-colors"
              data-testid="share-twitter"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X / Twitter
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent('Join me on LustLink — premium adult content platform where creators keep 90%! 🔥')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-[#0088cc]/20 hover:text-[#0088cc] text-slate-300 text-sm font-semibold rounded-xl transition-colors"
              data-testid="share-telegram"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              Telegram
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent('Join me on LustLink — premium adult content platform! Sign up here: ' + referralLink)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-[#25d366]/20 hover:text-[#25d366] text-slate-300 text-sm font-semibold rounded-xl transition-colors"
              data-testid="share-whatsapp"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            <a
              href={`https://reddit.com/submit?url=${encodeURIComponent(referralLink)}&title=${encodeURIComponent('LustLink — Premium adult content platform where creators keep 90% of revenue')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-[#ff4500]/20 hover:text-[#ff4500] text-slate-300 text-sm font-semibold rounded-xl transition-colors"
              data-testid="share-reddit"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
              Reddit
            </a>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={User} label="Total Referrals" value={stats?.total_referrals || 0} accent="text-blue-400" />
        <StatCard icon={ArrowsClockwise} label="Active" value={stats?.active_referrals || 0} accent="text-emerald-400" />
        <StatCard icon={Coins} label="Tokens Earned" value={stats?.total_earnings_tokens || 0} accent="text-amber-400" />
        <StatCard icon={Gift} label="Commissions" value={stats?.total_commission_events || 0} accent="text-indigo-400" />
      </div>

      {/* How it works */}
      <div className="glass-light rounded-2xl p-6 mb-6">
        <h3 className="text-white font-bold mb-4">How It Works</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { step: '1', title: 'Share your link', desc: 'Send your referral link to friends, post on socials, or share in communities.' },
            { step: '2', title: 'They sign up', desc: 'When someone registers through your link, they\'re automatically linked to your account.' },
            { step: '3', title: 'Earn 5% forever', desc: 'Every time they buy tokens, you earn 5% as commission. No time limit, no cap.' },
          ].map(item => (
            <div key={item.step} className="text-center">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 font-bold flex items-center justify-center mx-auto mb-2">
                {item.step}
              </div>
              <h4 className="text-white font-semibold text-sm">{item.title}</h4>
              <p className="text-slate-500 text-xs mt-1">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs: Referrals / Commissions */}
      <div className="flex gap-1 mb-4 bg-slate-900/50 rounded-xl p-1 w-fit" data-testid="referral-tabs">
        <button
          onClick={() => setTab('referrals')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            tab === 'referrals' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-500 hover:text-slate-300'
          }`}
          data-testid="tab-referrals"
        >
          Referred Users ({referrals.length})
        </button>
        <button
          onClick={() => setTab('commissions')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            tab === 'commissions' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-500 hover:text-slate-300'
          }`}
          data-testid="tab-commissions"
        >
          Commission History ({commissions.length})
        </button>
      </div>

      {/* Referrals List */}
      {tab === 'referrals' && (
        <div className="space-y-2">
          {referrals.length === 0 ? (
            <div className="text-center py-16 glass-light rounded-2xl">
              <ShareNetwork className="w-14 h-14 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500">No referrals yet. Share your link to get started!</p>
            </div>
          ) : (
            referrals.map((ref, idx) => (
              <motion.div
                key={ref.referred_id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="glass-light rounded-xl p-4 flex items-center gap-4"
                data-testid={`referral-item-${idx}`}
              >
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {ref.avatar_url ? (
                    <img src={ref.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-slate-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{ref.display_name}</p>
                  <p className="text-slate-500 text-xs">
                    {ref.role} &middot; Joined {new Date(ref.joined_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-amber-400 font-bold text-sm flex items-center gap-1">
                    <Coins weight="fill" className="w-4 h-4" /> {ref.earnings_tokens}
                  </p>
                  <p className={`text-xs ${ref.status === 'active' ? 'text-emerald-400' : 'text-slate-500'}`}>
                    {ref.status}
                  </p>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Commissions List */}
      {tab === 'commissions' && (
        <div className="space-y-2">
          {commissions.length === 0 ? (
            <div className="text-center py-16 glass-light rounded-2xl">
              <Coins className="w-14 h-14 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500">No commissions yet. Earn 5% on every purchase by your referrals.</p>
            </div>
          ) : (
            commissions.map((c, idx) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="glass-light rounded-xl p-4 flex items-center gap-4"
                data-testid={`commission-item-${idx}`}
              >
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <Coins weight="fill" className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold">
                    +{c.token_amount} tokens from {c.referred_name || 'User'}
                  </p>
                  <p className="text-slate-500 text-xs">
                    5% of {c.source_amount} token purchase &middot; {new Date(c.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-amber-400 font-bold text-sm flex-shrink-0">
                  +{c.token_amount}
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ReferralPage;
